import React, { useState, useEffect } from "react";
import { Table, Button, Modal, Form } from "react-bootstrap";
import { toast, ToastContainer } from "react-toastify";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import {
  addSpaService,
  getSpaService,
  updateSpaService,
  deleteSpaService,
  getHotelDetailsAPI,
} from "../../helpers/api.ts";

const SpaServices = () => {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState({});
  const [hotelGSTDetails, setHotelGSTDetails] = useState(null);

  const userString = localStorage.getItem("user");
  const user = userString ? JSON.parse(userString) : {};

  const hotel_id =
    user?.role === "HOTEL_ADMIN" ? user?.hotel_id : user?.hotel?.hotel_id;

  const fetchHotelGSTDetails = async () => {
    try {
      if (!hotel_id) { toast.error("Hotel ID is missing"); return; }
      const response = await getHotelDetailsAPI({ hotel_id });
      if (response?.data) setHotelGSTDetails(response.data);
    } catch (error) {
      toast.error("Failed to fetch hotel details");
    }
  };

  useEffect(() => {
    fetchHotelGSTDetails();
    fetchServices();
  }, []);

  const hasHotelGST = !!hotelGSTDetails?.gst_number;

  // ─────────────────────────────────────────────────────────────────────────
  // Table display: show last-used GST rate from DB (set during billing)
  // ─────────────────────────────────────────────────────────────────────────
  const calculateServicePrice = (service) => {
    const basePrice = parseFloat(service.price) || 0;
    const serviceGstRate = parseFloat(service.gst || 0);

    if (serviceGstRate > 0) {
      const gstAmount = parseFloat(((basePrice * serviceGstRate) / 100).toFixed(2));
      const finalAmount = parseFloat((basePrice + gstAmount).toFixed(2));
      return {
        basePrice: basePrice.toFixed(2),
        gstRate: serviceGstRate,
        gstAmount: gstAmount.toFixed(2),
        finalAmount: finalAmount.toFixed(2),
        hasGst: true,
      };
    }

    return {
      basePrice: basePrice.toFixed(2),
      gstRate: 0,
      gstAmount: "0.00",
      finalAmount: basePrice.toFixed(2),
      hasGst: false,
    };
  };

  // ─────────────────────────────────────────────────────────────────────────
  // CRUD
  // ─────────────────────────────────────────────────────────────────────────
  const fetchServices = async () => {
    try {
      const response = await getSpaService({ hotel_id });
      setServices(response.spaServices || []);
    } catch (error) {
      console.log(error);
    }
  };

  const handleShowModal = (service = null) => {
    setEditingService(service);
    setFormData(
      service
        ? {
            service_name: service.service_name || "",
            price: service.price || "",
            duration: service.duration || "",
            // ✅ gst intentionally excluded — managed at billing time only
          }
        : { service_name: "", price: "", duration: "" }
    );
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingService(null);
    setFormData({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // ✅ gst NOT sent — stays at whatever was last set during billing
      // New services default to gst: 0 in DB
      const serviceData = {
        service_name: formData.service_name,
        price: formData.price,
        duration: formData.duration,
      };

      if (editingService) {
        await updateSpaService(editingService.id, serviceData);
        toast.success("Service updated successfully");
      } else {
        await addSpaService({ ...serviceData, hotel_id });
        toast.success("Service added successfully");
      }
      fetchServices();
      handleCloseModal();
    } catch (error) {
      toast.error("Failed to save service");
      console.log(error);
    }
  };

  const handleDelete = async (id) => {
    const answer = await Swal.fire({
      icon: "warning",
      title: "Delete this service?",
      text: "This cannot be undone.",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#c0363f",
    });
    if (answer.isConfirmed) {
      try {
        await deleteSpaService(id);
        toast.success("Service deleted successfully");
        fetchServices();
      } catch (error) {
        toast.error("Failed to delete service");
      }
    }
  };

  return (
    <div className="container mt-4 px-3 py-5 px-md-5">
      <h1>Spa Services</h1>

      {/* Hotel GST registration status */}
      {hotelGSTDetails && (
        <div
          className="mb-2 p-2 rounded"
          style={{
            background: hasHotelGST ? "#e8f5e9" : "#fff3e0",
            border: `1px solid ${hasHotelGST ? "#1a7a3c" : "#e65100"}`,
            fontSize: "12px",
          }}
        >
          {hasHotelGST ? (
            <span style={{ color: "#1a7a3c" }}>
              ✓ Hotel GST registered:{" "}
              <strong>{hotelGSTDetails.gst_number}</strong>
            </span>
          ) : (
            <span style={{ color: "#e65100" }}>
              ⚠ Hotel GST not registered
            </span>
          )}
        </div>
      )}

      

      <div className="d-flex flex-column flex-md-row gap-2 mb-3">
        <Button
          variant="primary"
          onClick={() => handleShowModal()}
          className="w-100 w-md-auto"
        >
          Add New Service
        </Button>
        <Button
          variant="secondary"
          onClick={() => navigate(-1)}
          className="w-100 w-md-auto"
        >
          Back
        </Button>
      </div>

      <div className="table-responsive">
        <Table striped bordered hover>
          <thead>
            <tr>
              <th>Service Name</th>
              <th className="d-none d-lg-table-cell">Base Price</th>
             
              <th>Final Price</th>
              <th className="d-none d-md-table-cell">Duration (min)</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {services?.map((service) => {
              const p = calculateServicePrice(service);
              return (
                <tr key={service.id}>
                  <td>
                    <div className="d-flex flex-column">
                      <span>{service.service_name}</span>
                      <small className="text-muted d-md-none">
                        {service.duration} min
                      </small>
                    </div>
                  </td>

                  <td className="d-none d-lg-table-cell">₹{p.basePrice}</td>

                 

                  <td>
                    <strong>₹{p.finalAmount}</strong>
                   
                  </td>

                  <td className="d-none d-md-table-cell">{service.duration}</td>

                  <td>
                    <div className="d-flex gap-1">
                      <Button
                        variant="info"
                        size="sm"
                        onClick={() => handleShowModal(service)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleDelete(service.id)}
                      >
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>

      {/* ── Add / Edit Modal — NO GST FIELD ── */}
      <Modal
        show={showModal}
        onHide={handleCloseModal}
        className="modal-fullscreen-md-down"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {editingService ? "Edit Service" : "Add New Service"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>

          {/* Info note inside modal */}
          <div
            className="mb-3 p-2 rounded"
            style={{
              background: "#f0f4ff",
              border: "1px solid #c5cae9",
              fontSize: "12px",
              color: "#3949ab",
            }}
          >
            ℹ GST rate will be entered when creating the bill for this service.
          </div>

          <Form onSubmit={handleSubmit}>

            <Form.Group className="mb-3">
              <Form.Label>Service Name</Form.Label>
              <Form.Control
                type="text"
                name="service_name"
                value={formData.service_name || ""}
                onChange={handleInputChange}
                placeholder="e.g. Head Massage"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Base Price (₹)</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                min="0"
                name="price"
                value={formData.price || ""}
                onChange={handleInputChange}
                placeholder="e.g. 3000"
                required
              />
              <Form.Text className="text-muted">
                Enter base price only. GST will be applied at billing time.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Duration (minutes)</Form.Label>
              <Form.Control
                type="number"
                min="1"
                name="duration"
                value={formData.duration || ""}
                onChange={handleInputChange}
                placeholder="e.g. 60"
                required
              />
            </Form.Group>

            <div className="d-flex justify-content-end mt-3">
              <Button variant="primary" type="submit" className="w-100 w-md-auto">
                Save
              </Button>
            </div>

          </Form>
        </Modal.Body>
      </Modal>

      <ToastContainer />
    </div>
  );
};

export default SpaServices;